import React from 'react';
import type { SalesTransactionDiscountType } from '@/api/models';
import {
    DISCOUNT_NOTE_MAX_LENGTH,
    isDiscountValueValid,
    priceSale,
    toGel,
    type Discount,
    type LineToPrice,
} from '../../discountMath';
import type { CartLine } from '../../useSeller';
import { parseValueText, toFieldValue } from './discountDialogMeta';

/** What the open dialog discounts. */
export type DiscountTarget =
    { kind: 'line'; variantId: string } | { kind: 'sale' };

/** What is being discounted, for the summary block (GEL). */
export type DiscountSummary =
    | {
          kind: 'line';
          line: CartLine;
          quantity: number;
          unitPrice: number;
          listTotal: number;
      }
    | {
          kind: 'sale';
          itemCount: number;
          /** The subtotal less every line discount: what the sale discount applies to. */
          afterLines: number;
      };

/**
 * The live preview (GEL). `base` is what the discount applies to: the
 * line's list total, or the sale after line discounts. `empty`: no value
 * typed; `refused`: a value that cannot be used, so the figures are unknown.
 */
export type DiscountPreview =
    | { status: 'empty'; base: number }
    | { status: 'fits'; base: number; discount: number; total: number }
    | { status: 'refused'; base: number };

/**
 * Why the typed value cannot be applied, with the limit to name: a value of
 * the wrong shape, a line discount above the line's list total (`max`), a
 * sale discount that takes the whole remainder (`max`), or a discount that
 * leaves the sale at or below 0.00.
 */
export type DiscountDialogProblem =
    | { kind: 'valueInvalid'; type: SalesTransactionDiscountType }
    | { kind: 'lineTooLarge'; max: number }
    | { kind: 'saleTooLarge'; max: number }
    | { kind: 'nothingLeftToPay' };

type UseDiscountDialogParams = {
    cart: CartLine[];
    saleDiscount: Discount | null;
    setLineDiscount: (variantId: string, discount: Discount | null) => void;
    setSaleDiscount: (discount: Discount | null) => void;
    focusHome: () => void;
};

export type DiscountDialogState = {
    isOpen: boolean;
    /** Opens the dialog for one cart line; an unknown line is ignored. */
    openLine: (variantId: string) => void;
    openSale: () => void;
    /** Wired to the modal's `onOpenChange`: every closing route lands here, and none of them changes a discount. */
    onOpenChange: (open: boolean) => void;
    summary: DiscountSummary | null;
    type: SalesTransactionDiscountType;
    /** Switches the type; the typed value goes, since it meant the other kind. */
    selectType: (type: SalesTransactionDiscountType) => void;
    /** Changes whenever the value field must start over: a new opening, a type switch. */
    fieldKey: string;
    /** The value field's starting value, in the field's own terms (a fraction for a percentage). */
    fieldDefaultValue: number | undefined;
    /**
     * Every keystroke in the value field: the dialog's value is what was
     * typed, so the field's own commit (which rounds to its format) never
     * turns a refused value into a usable one.
     */
    onValueText: (text: string) => void;
    note: string;
    setNote: (note: string) => void;
    noteMaxLength: number;
    preview: DiscountPreview | null;
    problem: DiscountDialogProblem | null;
    /** An empty value applies too: it removes the discount. */
    canApply: boolean;
    apply: () => void;
    /** The dialog edits an existing discount, so it offers Remove. */
    canRemove: boolean;
    remove: () => void;
    /** The value field's input, focused once the dialog opens. */
    valueInputRef: React.RefObject<HTMLInputElement | null>;
};

type Draft = {
    target: DiscountTarget;
    /** The discount the target had when the dialog opened. */
    existing: Discount | null;
    type: SalesTransactionDiscountType;
    /** A percentage or GEL as typed; `null` for nothing, `NaN` for gibberish. */
    value: number | null;
    note: string;
    /** Bumped by every opening, so the field never keeps a previous value. */
    opening: number;
};

/** What the open dialog shows, from the draft and the cart. */
type Evaluation = {
    summary: DiscountSummary;
    preview: DiscountPreview;
    problem: DiscountDialogProblem | null;
};

const toLinesToPrice = (cart: CartLine[]): LineToPrice[] =>
    cart.map((line) => ({
        unitPrice: line.variant.price,
        quantity: line.qnt,
        discount: line.discount,
    }));

/**
 * The discount dialog's state (SEL-100): which line or the sale it is open
 * for, the draft (type, value, note), the live preview and why a value
 * cannot be used, all computed with the cart's own rule (`discountMath`) so
 * the dialog refuses exactly what bos-api would. Called by `useCartPanel`,
 * which opens it from `F6`/`F7`; `DiscountDialog` renders it.
 *
 * Applying sets the discount on the cart (`setLineDiscount`/
 * `setSaleDiscount`); an empty value removes it, as Remove does. Cancel, Esc
 * and the close trigger change nothing. A line that leaves the cart while
 * its dialog is open closes it.
 *
 * The value field takes focus once the dialog opens (the same next-task
 * deferral `useVoidConfirm` uses, which beats the dialog's own initial
 * focus), and focus goes home after any closing route.
 */
export const useDiscountDialog = ({
    cart,
    saleDiscount,
    setLineDiscount,
    setSaleDiscount,
    focusHome,
}: UseDiscountDialogParams): DiscountDialogState => {
    const [draft, setDraft] = React.useState<Draft | null>(null);
    const openings = React.useRef(0);
    const valueInputRef = React.useRef<HTMLInputElement>(null);

    const lineIndex =
        draft?.target.kind === 'line'
            ? cart.findIndex(
                  (line) =>
                      draft.target.kind === 'line' &&
                      line.variant.variantId === draft.target.variantId,
              )
            : -1;
    if (draft?.target.kind === 'line' && lineIndex === -1) {
        setDraft(null);
    }

    const begin = React.useCallback(
        (target: DiscountTarget, existing: Discount | null) => {
            openings.current += 1;
            setDraft({
                target,
                existing,
                type: existing?.type ?? 'percentage',
                value: existing?.value ?? null,
                note: existing?.note ?? '',
                opening: openings.current,
            });
        },
        [],
    );

    const openLine = React.useCallback(
        (variantId: string) => {
            const line = cart.find(
                (candidate) => candidate.variant.variantId === variantId,
            );
            if (line) {
                begin({ kind: 'line', variantId }, line.discount);
            }
        },
        [cart, begin],
    );

    const openSale = React.useCallback(
        () => begin({ kind: 'sale' }, saleDiscount),
        [begin, saleDiscount],
    );

    const onOpenChange = React.useCallback((open: boolean) => {
        if (!open) {
            setDraft(null);
        }
    }, []);

    const selectType = React.useCallback(
        (type: SalesTransactionDiscountType) =>
            setDraft((prev) =>
                prev === null || prev.type === type
                    ? prev
                    : { ...prev, type, value: null },
            ),
        [],
    );

    const onValueText = React.useCallback(
        (text: string) =>
            setDraft((prev) =>
                prev === null ? prev : { ...prev, value: parseValueText(text) },
            ),
        [],
    );

    const setNote = React.useCallback(
        (note: string) =>
            setDraft((prev) =>
                prev === null
                    ? prev
                    : {
                          ...prev,
                          note: note.slice(0, DISCOUNT_NOTE_MAX_LENGTH),
                      },
            ),
        [],
    );

    const candidate = React.useMemo(
        (): Discount | null =>
            draft === null || draft.value === null
                ? null
                : { type: draft.type, value: draft.value, note: draft.note },
        [draft],
    );

    const evaluation = React.useMemo((): Evaluation | null => {
        if (draft === null) {
            return null;
        }
        const lines = toLinesToPrice(cart);

        if (draft.target.kind === 'line') {
            const line = cart[lineIndex];
            if (!line) {
                return null;
            }
            const priced = priceSale(
                lines.map((item, index) =>
                    index === lineIndex
                        ? { ...item, discount: candidate }
                        : item,
                ),
                saleDiscount,
            );
            const own = priced.lines[lineIndex]!;
            const base = toGel(own.listTotalTetri);
            const summary: DiscountSummary = {
                kind: 'line',
                line,
                quantity: line.qnt,
                unitPrice: line.variant.price,
                listTotal: base,
            };
            if (candidate === null) {
                return {
                    summary,
                    preview: { status: 'empty', base },
                    problem: null,
                };
            }
            const problem: DiscountDialogProblem | null =
                own.fit === 'valueInvalid'
                    ? { kind: 'valueInvalid', type: candidate.type }
                    : own.fit === 'lineTooLarge'
                      ? { kind: 'lineTooLarge', max: base }
                      : priced.totalTetri <= 0
                        ? { kind: 'nothingLeftToPay' }
                        : null;
            return {
                summary,
                preview:
                    problem === null
                        ? {
                              status: 'fits',
                              base,
                              discount: toGel(own.discountTetri),
                              total: toGel(own.afterDiscountTetri),
                          }
                        : { status: 'refused', base },
                problem,
            };
        }

        const priced = priceSale(lines, candidate);
        const base = toGel(priced.afterLinesTetri);
        const summary: DiscountSummary = {
            kind: 'sale',
            itemCount: cart.reduce((sum, line) => sum + line.qnt, 0),
            afterLines: base,
        };
        if (candidate === null) {
            return {
                summary,
                preview: { status: 'empty', base },
                problem: null,
            };
        }
        const remainder = priced.afterLinesTetri - priced.saleDiscountTetri;
        const problem: DiscountDialogProblem | null = !isDiscountValueValid(
            candidate,
        )
            ? { kind: 'valueInvalid', type: candidate.type }
            : remainder <= 0
              ? candidate.type === 'fixed'
                  ? { kind: 'saleTooLarge', max: base }
                  : { kind: 'nothingLeftToPay' }
              : null;
        return {
            summary,
            preview:
                problem === null
                    ? {
                          status: 'fits',
                          base,
                          discount: toGel(priced.saleDiscountTetri),
                          total: toGel(remainder),
                      }
                    : { status: 'refused', base },
            problem,
        };
    }, [draft, cart, lineIndex, candidate, saleDiscount]);

    const isOpen = draft !== null && evaluation !== null;
    const canApply = isOpen && evaluation.problem === null;

    // What the dialog last showed, kept once it closes: the modal stays
    // mounted while it animates out, and without it the body would empty, a
    // sale dialog's title fall back to the line's and Apply dim (QA #100).
    // Only what is shown stays: every action reads the live draft, so none
    // does anything while closed.
    const [lastShown, setLastShown] = React.useState<{
        draft: Draft;
        evaluation: Evaluation;
    } | null>(null);
    if (
        isOpen &&
        (lastShown?.draft !== draft || lastShown.evaluation !== evaluation)
    ) {
        setLastShown({ draft, evaluation });
    }
    const shownDraft = isOpen ? draft : (lastShown?.draft ?? null);
    const shownEvaluation = isOpen
        ? evaluation
        : (lastShown?.evaluation ?? null);
    const problem = shownEvaluation?.problem ?? null;

    const commit = React.useCallback(
        (discount: Discount | null) => {
            if (draft === null) {
                return;
            }
            if (draft.target.kind === 'line') {
                setLineDiscount(draft.target.variantId, discount);
            } else {
                setSaleDiscount(discount);
            }
            setDraft(null);
        },
        [draft, setLineDiscount, setSaleDiscount],
    );

    const apply = React.useCallback(() => {
        if (canApply) {
            commit(candidate);
        }
    }, [canApply, commit, candidate]);

    const remove = React.useCallback(() => commit(null), [commit]);

    // Once per opening, not on every keystroke.
    const opening = draft?.opening ?? null;
    React.useEffect(() => {
        if (opening === null) {
            return;
        }
        const timeout = setTimeout(() => valueInputRef.current?.focus(), 0);
        return () => clearTimeout(timeout);
    }, [opening]);

    const wasOpen = React.useRef(isOpen);
    React.useEffect(() => {
        if (wasOpen.current && !isOpen) {
            focusHome();
        }
        wasOpen.current = isOpen;
    }, [isOpen, focusHome]);

    const type = shownDraft?.type ?? 'percentage';

    return {
        isOpen,
        openLine,
        openSale,
        onOpenChange,
        summary: shownEvaluation?.summary ?? null,
        type,
        selectType,
        fieldKey: `${shownDraft?.opening ?? 0}-${type}`,
        fieldDefaultValue: toFieldValue(type, shownDraft?.value ?? null),
        onValueText,
        note: shownDraft?.note ?? '',
        setNote,
        noteMaxLength: DISCOUNT_NOTE_MAX_LENGTH,
        preview: shownEvaluation?.preview ?? null,
        problem,
        canApply: shownEvaluation !== null && problem === null,
        apply,
        canRemove: shownDraft?.existing != null,
        remove,
        valueInputRef,
    };
};
